import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTicketAlt, FaCalendarAlt, FaHistory, FaEdit, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchCoupons, deleteCoupon, clearState } from "../../redux/Slices/CouponsSlice";
import { useTitle } from "../../context/TitleContext";
import PermissionMotionButton from "../PermissionMotionButton";
import PermissionButton from "../PermissionButton";
import Pagination from '../../components/Pagination';

const Coupons = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCoupon, setDeletingCoupon] = useState(null);

  const { coupons, meta, isLoading, error, success } = useSelector(
    (state) => state.coupons
  );

  useEffect(() => {
    setTitle("الكوبونات");
  }, [setTitle]);

  useEffect(() => {
    dispatch(clearState());
    dispatch(fetchCoupons({ page: currentPage, per_page: 10 }));
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (success) {
      setShowDeleteModal(false);
      setDeletingCoupon(null);
      setTimeout(() => {
        toast.success(success || "تم الحذف بنجاح", { position: "top-center", zIndex: 9999 });
      }, 100);
      dispatch(fetchCoupons({ page: currentPage, per_page: 10 }));
      dispatch(clearState());
    }
    if (error) {
      toast.error(typeof error === 'string' ? error : "حدث خطأ ما", { position: "top-center", zIndex: 9999 });
      dispatch(clearState());
    }
  }, [success, error, dispatch, currentPage]);

  const handlePageChange = (page) => {
    if (isLoading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };

  const handleDelete = (coupon) => {
    setDeletingCoupon(coupon);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deletingCoupon) {
      dispatch(deleteCoupon(deletingCoupon.id));
    }
  };



  const formatDate = (dateString) => {
    if (!dateString) return "مفتوح";
    return new Date(dateString).toLocaleDateString('ar-EG', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            🎫 الكوبونات
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل وإضافة كوبونات الخصم للعملاء</p>
        </div>
        <PermissionMotionButton
          permission="create-coupons"
          onClick={() => navigate("/add-coupon")}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
        >
          <FaPlus size={12} /> إضافة كوبون جديد
        </PermissionMotionButton>
      </div>

      {/* ── Loading Skeleton ────────────────────────────── */}
      {isLoading && (!coupons || !Array.isArray(coupons.data) || coupons.data.length === 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ height: 160, borderRadius: 16, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </div>
      )}

      {/* ── Empty ─────────────────────────────────────── */}
      {!isLoading && (!coupons || !Array.isArray(coupons.data) || coupons.data.length === 0) && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 52 }}>🎫</div>
          <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد كوبونات مسجلة</p>
        </div>
      )}

      {/* ── Coupons Voucher Cards Grid ─────────────────── */}
      {!isLoading && coupons?.data?.length > 0 && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 20 }}>
            {coupons.data.map((coupon, idx) => {
              const isActive = coupon.status === "active" || coupon.status === 1 || coupon.status === "1";
              return (
                <div key={coupon.id}
                  style={{
                    position: "relative",
                    background: "#fff",
                    borderRadius: 16,
                    border: "1px solid #e2e8f0",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.02)",
                    transition: "transform .2s, box-shadow .2s"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(99,102,241,.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.02)";
                  }}
                >
                  {/* Coupon Top Header (Voucher look) */}
                  <div style={{
                    padding: "16px 20px",
                    background: isActive ? "linear-gradient(135deg, #f5f3ff, #ede9fe)" : "#f8fafc",
                    borderBottom: "1.5px dashed #e2e8f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    {/* Ticket Code Tag */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <FaTicketAlt size={16} style={{ color: isActive ? "var(--main-color)" : "#94a3b8" }} />
                      <span style={{
                        fontSize: 14,
                        fontWeight: 800,
                        letterSpacing: "0.5px",
                        color: isActive ? "#4338ca" : "#475569",
                        background: "#fff",
                        padding: "4px 10px",
                        borderRadius: 8,
                        border: "1px solid " + (isActive ? "#ddd6fe" : "#cbd5e1"),
                        boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
                      }}>
                        {coupon.code}
                      </span>
                    </div>

                    {/* Status badge */}
                    {isActive ? (
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#15803d", background: "#d1fae5", padding: "2px 10px", borderRadius: 20 }}>مفعل</span>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", background: "#f1f5f9", padding: "2px 10px", borderRadius: 20 }}>معطل</span>
                    )}
                  </div>

                  {/* Tear-off Ticket circles (UI decoration) */}
                  <div style={{ position: "absolute", top: 48, right: -8, width: 16, height: 16, borderRadius: "50%", background: "#f8fafc", border: "1px solid #e2e8f0", borderTopColor: "transparent", borderRightColor: "transparent", zIndex: 10 }} />
                  <div style={{ position: "absolute", top: 48, left: -8, width: 16, height: 16, borderRadius: "50%", background: "#f8fafc", border: "1px solid #e2e8f0", borderTopColor: "transparent", borderLeftColor: "transparent", zIndex: 10 }} />

                  {/* Voucher Body */}
                  <div style={{ padding: "16px 20px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 16 }}>
                    {/* Voucher Discount Price Value */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>قيمة الخصم</span>
                      <h2 style={{ margin: 0, fontSize: 26, fontWeight: 900, color: isActive ? "var(--main-color)" : "#475569" }}>
                        {coupon.discount_value} 
                        <span style={{ fontSize: 14, fontWeight: 700, marginRight: 4, color: "#64748b" }}>
                          {coupon.discount_type === "percentage" ? "% خصم مئوي" : "ج.م خصم ثابت"}
                        </span>
                      </h2>
                    </div>

                    {/* Expiration + Limits Row */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#64748b", fontSize: 12 }}>
                        <FaCalendarAlt size={12} style={{ color: "#94a3b8" }} />
                        <span>ينتهي: {formatDate(coupon.ends_at)}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#64748b", fontSize: 12 }}>
                        <FaHistory size={12} style={{ color: "#94a3b8" }} />
                        <span>الحد: {coupon.usage_limit || "غير محدود"}</span>
                      </div>
                    </div>

                    {/* Actions row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #f1f5f9", paddingTop: 12, marginTop: 4 }}>
                      <span style={{ fontSize: 11, color: "#cbd5e1" }}>#{meta ? (meta.current_page - 1) * (meta.per_page || 10) + idx + 1 : idx + 1}</span>
                      <div style={{ display: "flex", gap: 6 }}>
                        <PermissionButton permission="edit-coupons" onClick={() => navigate(`/edit-coupon/${coupon.id}`)} title="تعديل"
                          style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                          <FaEdit size={14} />
                        </PermissionButton>
                        <PermissionButton permission="delete-coupons" onClick={() => handleDelete(coupon)} disabled={isLoading} title="حذف"
                          style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                          <FaTrash size={14} />
                        </PermissionButton>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <Pagination meta={meta} currentPage={currentPage} loading={isLoading} onChange={handlePageChange} />
        </>
      )}

      {/* ── Delete Modal ──────────────────────────────── */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingCoupon(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف هذا الكوبون؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                🎫 {deletingCoupon?.code}
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingCoupon(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} disabled={isLoading} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {isLoading ? <><span className="spinner-border spinner-border-sm"></span> جاري الحذف...</> : "حذف الكوبون"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coupons;
